import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Waves, Bath, Umbrella, Gamepad2, ChefHat, Wifi, Snowflake, Flame, TreePine, Truck, Bird, Mountain } from "lucide-react";

const AMENITY_ICONS = [Waves, Bath, Umbrella, Gamepad2, ChefHat, Wifi, Snowflake, Flame, TreePine, Truck, Bird, Mountain];

export const Stats = ({ stats }) => (
  <section className="bg-sand" aria-label="Property at a glance" data-testid="stats-band">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-10 grid grid-cols-2 sm:grid-cols-5 gap-y-8">
      {stats.map((s, i) => (
        <motion.div
          key={s.label}
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
          className={`text-center ${i > 0 ? "sm:border-l sm:border-charcoal/15" : ""}`}
          data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <p className="font-serif text-4xl sm:text-5xl font-semibold text-copperdeep">{s.value}</p>
          <p className="text-xs uppercase tracking-[0.2em] text-soft font-semibold mt-1.5">{s.label}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

export const Marquee = ({ items }) => (
  <div className="bg-charcoal py-4 overflow-hidden" aria-hidden data-testid="marquee-band">
    <div className="marquee-track flex whitespace-nowrap">
      {[...items, ...items].map((item, i) => (
        <span key={i} className="inline-flex items-center text-cream/90 font-serif italic text-lg">
          <span className="px-6">{item}</span>
          <span className="text-copper">✦</span>
        </span>
      ))}
    </div>
  </div>
);

export const Gallery = ({ photos, credit }) => {
  const [lightbox, setLightbox] = useState(null);
  const reduceMotion = useReducedMotion();
  return (
    <section id="gallery" className="scroll-mt-24 py-16 sm:py-24" aria-label="Photo gallery" data-testid="landing-gallery">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.24em] text-copper font-semibold mb-2">The Farm</p>
          <h2 className="font-serif text-3xl sm:text-5xl font-semibold text-charcoal">A slow walk <em className="text-copperdeep">around</em></h2>
          <p className="text-soft text-lg mt-3 max-w-2xl">From the waterfall pool to the covered patio and the game-room garage — every corner was made for gathering.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 auto-rows-[150px] sm:auto-rows-[210px] gap-3 sm:gap-4">
          {photos.map((photo, i) => (
            <motion.button
              key={photo.url}
              initial={reduceMotion ? false : { opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 4) * 0.08 }}
              onClick={() => setLightbox(i)}
              className={`relative overflow-hidden rounded-2xl group ${i === 0 ? "col-span-2 row-span-2" : ""} ${i === 4 ? "sm:col-span-2" : ""}`}
              data-testid={`landing-gallery-item-${i}`}
              aria-label={`View photo: ${photo.label}`}
            >
              <img src={photo.url} alt={photo.label} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/20 transition-colors duration-500" aria-hidden />
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/70 to-transparent p-3.5 text-left opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="text-cream text-sm font-semibold">{photo.label}</span>
              </span>
            </motion.button>
          ))}
        </div>
        <p className="text-xs text-soft mt-4">{credit}</p>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-charcoal/95 flex items-center justify-center"
            role="dialog" aria-modal="true" aria-label="Photo viewer"
            data-testid="landing-lightbox" onClick={() => setLightbox(null)}>
            <button className="absolute top-4 right-4 p-3 text-cream hover:text-copper transition-colors" aria-label="Close" data-testid="landing-lightbox-close"><X size={26} /></button>
            <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + photos.length) % photos.length); }}
              className="absolute left-3 sm:left-8 p-3 text-cream hover:text-copper transition-colors" aria-label="Previous" data-testid="landing-lightbox-prev"><ChevronLeft size={30} /></button>
            <motion.figure key={lightbox} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
              className="max-w-5xl w-full px-14" onClick={(e) => e.stopPropagation()}>
              <img src={photos[lightbox].url} alt={photos[lightbox].label} className="w-full max-h-[78vh] object-contain rounded-xl" />
              <figcaption className="text-center text-cream/85 mt-3 font-serif italic text-lg">{photos[lightbox].label}</figcaption>
            </motion.figure>
            <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % photos.length); }}
              className="absolute right-3 sm:right-8 p-3 text-cream hover:text-copper transition-colors" aria-label="Next" data-testid="landing-lightbox-next"><ChevronRight size={30} /></button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export const Amenities = ({ amenities }) => (
  <section id="amenities" className="scroll-mt-24 bg-creamdark py-16 sm:py-24" aria-label="Amenities" data-testid="amenities-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.24em] text-copper font-semibold mb-2">Everything Included</p>
        <h2 className="font-serif text-3xl sm:text-5xl font-semibold text-charcoal">Farm-grade <em className="text-copperdeep">comforts</em></h2>
        <p className="text-soft text-lg mt-3 max-w-2xl">Made for groups — flexible spaces, slow mornings, and room to spread out across 8 rustic acres.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {amenities.map((a, i) => {
          const Icon = AMENITY_ICONS[i % AMENITY_ICONS.length];
          return (
            <motion.div
              key={a}
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }} transition={{ delay: (i % 4) * 0.07, duration: 0.5 }}
              className="bg-white border border-charcoal/10 rounded-2xl px-5 py-5 flex items-center gap-3.5 hover:border-copper hover:shadow-md transition-all group"
              data-testid={`amenity-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 28)}`}
            >
              <span className="p-2.5 rounded-full bg-sand group-hover:bg-copper/15 transition-colors shrink-0">
                <Icon size={19} className="text-copperdeep" aria-hidden />
              </span>
              <span className="font-semibold text-charcoal text-base leading-snug">{a}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);
